import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import DOMPurify from 'dompurify';

// Animazioni
const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const TTSContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60vh;
  background-color: #fff;
  padding: 24px 16px 16px 16px;
  box-shadow: 0 -2px 15px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  animation: ${props => props.$isClosing ? slideDown : slideUp} 0.3s ease-out;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1001;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
  gap: 12px;
`;

const Title = styled.h3`
  font-weight: bold;
  font-size: 1.4rem;
  margin: 0;
  flex: 1;
  padding-right: 32px;
`;

const TextDisplay = styled.div`
  flex: 1;
  overflow-y: auto;
  font-size: 1rem;
  line-height: 1.6;
  padding: 10px;
  scroll-behavior: smooth;
`;

const Word = styled.span`
  background-color: ${({ highlighted }) => (highlighted ? 'rgba(0,123,255,0.3)' : 'transparent')};
  transition: background-color 0.3s ease;
`;

const Icon = ({ name }) => (
  <span className="material-symbols-outlined">{name}</span>
);

const TTSComponent = ({ text, onClose, language = 'it-IT', title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [words, setWords] = useState([]);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [segments, setSegments] = useState([]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [ttsSupported, setTtsSupported] = useState(true);
  const [simulatedPause, setSimulatedPause] = useState(false);
  const [resumeCharOffset, setResumeCharOffset] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  const utteranceRef = useRef(null);
  const fullTextRef = useRef('');
  const textDisplayRef = useRef(null);
  const wordRefs = useRef([]);
  const stoppedManuallyRef = useRef(false);

  useEffect(() => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(text);
    const cleanText = tempDiv.textContent || tempDiv.innerText || '';
    fullTextRef.current = cleanText;

    // Divide in parole mantenendo gli indici dei caratteri
    const regex = /\S+\s*/g;
    const parsedWords = [];
    let match;

    while ((match = regex.exec(cleanText)) !== null) {
      parsedWords.push({
        word: match[0],
        start: match.index,
        end: match.index + match[0].length
      });
    }

    setWords(parsedWords);

    // Suddividi il testo in frasi (segmenti)
    const splitRegex = /[^.!?]+[.!?]+[\])'"`'"]*|.+/g;
    const segs = cleanText.match(splitRegex) || [cleanText];
    setSegments(segs);
    setCurrentSegment(0);
  }, [text]);

  useEffect(() => {
    // Debug: stampa le voci disponibili
    const voices = window.speechSynthesis.getVoices();
    console.log('Voci disponibili:', voices.map(v => `${v.name} (${v.lang})`));
  }, []);

  // Evidenzia solo le parole che INIZIANO nel segmento corrente
  const getCurrentSegmentWordIndexes = () => {
    if (!segments.length) return [];
    const start = segments.slice(0, currentSegment).reduce((acc, seg) => acc + seg.length, 0);
    const end = start + segments[currentSegment].length;
    return words
      .map((w, idx) => (w.start >= start && w.start < end ? idx : null))
      .filter(idx => idx !== null);
  };

  const renderHighlightedText = () => {
    const highlightedIndexes = getCurrentSegmentWordIndexes();
    return words.map((word, idx) => (
      <Word
        key={idx}
        highlighted={highlightedIndexes.includes(idx)}
        ref={el => (wordRefs.current[idx] = el)}
      >
        {word.word}
      </Word>
    ));
  };

  // Scroll automatico SOLO nel contenitore TTS
  useEffect(() => {
    const highlightedIndexes = getCurrentSegmentWordIndexes();
    if (
      highlightedIndexes.length > 0 &&
      wordRefs.current[highlightedIndexes[0]] &&
      textDisplayRef.current
    ) {
      const wordEl = wordRefs.current[highlightedIndexes[0]];
      const container = textDisplayRef.current;
      const wordOffsetTop = wordEl.offsetTop;
      const wordOffsetHeight = wordEl.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      if (
        wordOffsetTop < containerScrollTop ||
        wordOffsetTop + wordOffsetHeight > containerScrollTop + containerHeight
      ) {
        container.scrollTop = wordOffsetTop - containerHeight / 2 + wordOffsetHeight / 2;
      }
    }
  }, [currentSegment, words]);

  useEffect(() => {
    // Verifica se onboundary è supportato
    let supported = false;
    try {
      const test = new SpeechSynthesisUtterance('test');
      supported = 'onboundary' in test;
    } catch (e) {
      supported = false;
    }
    setTtsSupported(supported);
  }, []);

  const getCurrentWordIndex = () => {
    return words.findIndex(w => currentCharIndex >= w.start && currentCharIndex < w.end);
  };

  // Funzione per selezionare la voce migliore per la lingua richiesta
  const getBestVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const langPrefix = language.split('-')[0];
    const langVoices = voices.filter(v => v.lang && v.lang.startsWith(langPrefix));

    if (langVoices.length === 0) return null;

    const preferredNames = {
      'de': ['Anna', 'Sara', 'Shelley', 'Sandy'],
      'it': ['Alice', 'Luca', 'Federica', 'Paola'],
      'es': ['Monica', 'Jorge', 'Paulina', 'Montse', 'Mónica'],
      'fr': ['Thomas', 'Amelie', 'Audrey', 'Sebastien', 'Amélie', 'Jacques'],
      'en': [
        'Google US English', 'Google UK English Female', 'Google UK English Male',
        'Samantha', 'Daniel', 'Karen', 'Moira', 'Serena', 'Victoria', 'Alex', 'Fred', 'Tessa'
      ],
    };

    if (preferredNames[langPrefix]) {
      for (const name of preferredNames[langPrefix]) {
        const found = langVoices.find(v => v.name.toLowerCase().includes(name.toLowerCase()));
        if (found) return found;
      }
    }

    const blacklist = [
      'grandma', 'grandpa', 'rocko', 'eddy', 'reed', 'flo', 'sandy', 'shelley', 'campane',
      'giullare', 'organo', 'violoncelli', 'zarvox', 'superstar', 'trinoid', 'boing', 'sussurro',
      'buone notizie', 'tremolio', 'brutte notizie', 'bollicine'
    ];
    const filtered = langVoices.filter(v => !blacklist.some(bad => v.name.toLowerCase().includes(bad)));

    if (filtered.length > 0) return filtered[0];

    return langVoices[0];
  };

  const playSegment = (index = 0, charOffset = 0) => {
    if (!segments.length || !window.speechSynthesis) return;
    stoppedManuallyRef.current = false;
    window.speechSynthesis.cancel();

    setCurrentCharIndex(0);
    setCurrentSegment(index);

    const segmentText = segments[index].slice(charOffset);
    const utterance = new SpeechSynthesisUtterance(segmentText);
    utterance.lang = language;
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    const bestVoice = getBestVoice();
    if (bestVoice) utterance.voice = bestVoice;

    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        const globalCharIndex =
          segments.slice(0, index).reduce((acc, seg) => acc + seg.length, 0) +
          charOffset +
          event.charIndex;
        setCurrentCharIndex(globalCharIndex);
      }
    };

    utterance.onend = () => {
      setCurrentCharIndex(0);

      if (!stoppedManuallyRef.current && index < segments.length - 1) {
        setResumeCharOffset(0);
        playSegment(index + 1, 0);
      } else {
        setIsPlaying(false);
        setIsPaused(false);
        setResumeCharOffset(0);
      }
    };

    utterance.onerror = (e) => {
      setIsPlaying(false);
      const isInterrupted = e?.error === 'interrupted' || e?.error === 'canceled';
      if (!stoppedManuallyRef.current && !isInterrupted) {
        alert(
          'La riproduzione della sintesi vocale non è supportata o non è partita correttamente su questo dispositivo/browser.'
        );
      }
    };

    utteranceRef.current = utterance;
    try {
      window.speechSynthesis.speak(utterance);
      setIsPlaying(true);
      setIsPaused(false);
    } catch (e) {
      alert(
        'La riproduzione della sintesi vocale non è supportata o non è partita correttamente su questo dispositivo/browser.'
      );
    }
  };

  const play = () => {
    setSimulatedPause(false);
    playSegment(currentSegment, resumeCharOffset);
  };

  const togglePause = () => {
    if (!window.speechSynthesis) return;
    if (isPlaying && !isPaused) {
      setSimulatedPause(true);
      stoppedManuallyRef.current = true;
      window.speechSynthesis.cancel();
      setIsPaused(true);
      setIsPlaying(false);

      const start = segments.slice(0, currentSegment).reduce((acc, seg) => acc + seg.length, 0);
      setResumeCharOffset(currentCharIndex > start ? currentCharIndex - start : 0);
    } else if (!isPlaying && isPaused) {
      setIsPaused(false);
      setSimulatedPause(false);
      playSegment(currentSegment, resumeCharOffset);
    }
  };

  const stop = () => {
    if (!window.speechSynthesis) return;
    stoppedManuallyRef.current = true;
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentCharIndex(0);
    setCurrentSegment(0);
    setResumeCharOffset(0);
  };

  const handleClose = () => {
    stoppedManuallyRef.current = true;
    stop();
    setIsClosing(true);
    
    // Aspetta che l'animazione finisca prima di chiamare onClose
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <TTSContainer $isClosing={isClosing}>
      {/* Pulsante Close posizionato in modo assoluto */}
      <CloseButton
        onClick={handleClose}
        className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm opacity-75"
        style={{ width: '32px', height: '32px' }}
        aria-label="Chiudi"
      >
        <Icon name="close" />
      </CloseButton>

      {!ttsSupported && (
        <div style={{ color: 'red', marginBottom: 8 }}>
          La funzione di evidenziazione parola non è supportata su questo browser.
        </div>
      )}
      
      <Header>
        {/* Pulsante Play/Pausa più grande a sinistra */}
        {!isPlaying ? (
          <button 
            onClick={play}
            className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: '56px', height: '56px', fontSize: '28px' }}
          >
            <Icon name="play_arrow" />
          </button>
        ) : (
          <button 
            onClick={togglePause}
            className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: '56px', height: '56px', fontSize: '28px' }}
          >
            <Icon name={isPaused ? 'play_arrow' : 'pause'} />
          </button>
        )}

        {/* Titolo a sinistra */}
        <Title>{title}</Title>
      </Header>
      <TextDisplay ref={textDisplayRef}>{renderHighlightedText()}</TextDisplay>
    </TTSContainer>
  );
};

export default TTSComponent;
